import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  DisableSettingsEdit,
  EnableSettingsEdit,
  SaveSettingsEdit,
  SettingsEditState,
} from '@ops/services';
import {
  CompanyService,
  ProjectManagerService,
  SessionManagerService,
} from '@ops/shared-types';
import { Observable, Subscription } from 'rxjs';

@Component({
  selector: 'ops-settings',
  templateUrl: './ops-settings.component.html',
  styleUrl: './ops-settings.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class OpsSettingsComponent implements OnInit, OnDestroy {
  editState$: Observable<boolean>;
  editSaveTimeout;
  userRole = 'guest';

  userHaveCompanies = false;
  companyMissingSubscription = false;
  subs: Array<Subscription> = [];

  constructor(
    private store: Store,
    private router: Router,
    private sessionManager: SessionManagerService,
    private companyService: CompanyService,
    private pm: ProjectManagerService,
    private translateService: TranslateService
  ) {}

  isActiveTabToHighlight(linkUrl: string) {
    const paramsUrl = this.router?.url?.split('/');
    return paramsUrl[paramsUrl.length - 1] === linkUrl;
  }

  ngOnInit(): void {
    this.editState$ = this.store.select(SettingsEditState.getEditState);
    this.applyEditModeByRoute();

    this.companyService.getCompanyFilters().subscribe((companies) => {
      if (companies?.length > 0) {
        this.userHaveCompanies = true;
      } else {
        this.userHaveCompanies = false;
      }
    });

    const urSub = this.sessionManager
      .getUserRoleObservable()
      .subscribe((userRole: string) => {
        this.userRole = userRole;
      });

    this.subs.push(urSub);

    const pSub = this.pm
      .getProjectSubscriptionMissingObservable()
      .subscribe((resp) => {
        // console.log('RESTRICTION_SHOW SETTINGS');
        this.companyMissingSubscription = resp;
      });

    this.subs.push(pSub);

    // Keep edit mode in sync with route for always-editable tabs
    const rSub = this.router.events.subscribe((evt) => {
      if (evt instanceof NavigationEnd) {
        this.applyEditModeByRoute();
      }
    });
    this.subs.push(rSub);
  }

  isVisibleByRole(tab: string) {
    if (tab === 'subscription') {
      return true;
      // return this.userRole === 'admin';
    }
    if (
      this.companyMissingSubscription &&
      tab !== 'profile' &&
      tab !== 'security' &&
      tab !== 'subscription'
    ) {
      return false;
    }

    if (
      !this.userHaveCompanies &&
      (tab === 'org' || tab === 'team' || tab === 'payments')
    ) {
      return false;
    }

    if (
      this.userRole === 'admin' ||
      this.userRole === 'staff' ||
      this.userRole === 'user'
    ) {
      return true;
    }

    if (this.userRole === 'guest' && tab === 'team') {
      return true;
    }

    return false;
  }

  get isTeamsTab(): boolean {
    if (this.router.url === '/settings/team-member') {
      return true;
    }

    return false;
  }

  get isSubscriptionsTab(): boolean {
    return this.router.url === '/settings/my-subscriptions';
  }

  get isJpLang(): boolean {
    return this.translateService.currentLang === 'jp';
  }

  get isSrLang(): boolean {
    return this.translateService.currentLang === 'sr';
  }

  get isAllowedForRole(): boolean {
    // Always hide buttons on Team and My Subscriptions tabs
    if (this.isTeamsTab || this.isSubscriptionsTab) {
      return false;
    }
    if (this.userRole === 'admin') {
      return true;
    }
    if (
      this.router.url === '/settings/profile-settings' ||
      this.router.url === '/settings/security' ||
      this.router.url === '/settings/my-subscriptions'
    ) {
      return true;
    }

    return false;
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  enableEdit() {
    this.store.dispatch(
      new EnableSettingsEdit({ isEditing: true, saveChanges: false })
    );
  }

  cancelEdit() {
    // Do not allow disabling edit on always-editable tabs
    if (this.isTeamsTab || this.isSubscriptionsTab) {
      this.store.dispatch(
        new EnableSettingsEdit({ isEditing: true, saveChanges: false })
      );
      return;
    }
    this.store.dispatch(new DisableSettingsEdit());
  }

  saveChanges() {
    if (this.editSaveTimeout) {
      clearTimeout(this.editSaveTimeout);
    }
    this.editSaveTimeout = setTimeout(() => {
      this.store.dispatch(new SaveSettingsEdit({ saveChanges: true }));
      // Keep edit mode enabled on always-editable tabs
      if (this.isTeamsTab || this.isSubscriptionsTab) {
        this.store.dispatch(
          new EnableSettingsEdit({ isEditing: true, saveChanges: false })
        );
      }
    }, 250);
  }

  private applyEditModeByRoute(): void {
    if (this.isTeamsTab || this.isSubscriptionsTab) {
      this.store.dispatch(
        new EnableSettingsEdit({ isEditing: true, saveChanges: false })
      );
    } else {
      this.store.dispatch(new DisableSettingsEdit());
    }
  }
}
